import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ClipLoader } from "react-spinners";

import { LuCheck, LuUploadCloud, LuX, LuBell, LuSend } from "react-icons/lu";
import { FiTrash2, FiX } from "react-icons/fi";

import ListPageShell from "../list-page/ListPageShell";
import ListPageHeader from "../list-page/ListPageHeader";

import { openModalWithTimeout } from "../../store/alertSlice";
import {
  useGetNotificationTypesQuery,
  useLazySearchUsersQuery,
  useSendNotificationMutation,
} from "../../store/services/notificationServices";
import { useGetMunicipalitiesQuery } from "../../store/services/municipalitiesServices";

export const removeHtmlTags = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

export const categories = [
  "Car equipment",
  "Car maintenance",
  "Car purchase",
  "Car sales",
  "Camping tips",
];

export const cities = [
  { name: "Prishtinë", zip_code: "10000" },
  { name: "Mitrovicë", zip_code: "40000" },
  { name: "Pejë", zip_code: "30000" },
  { name: "Prizren", zip_code: "20000" },
  { name: "Ferizaj", zip_code: "70000" },
  { name: "Gjilan", zip_code: "60000" },
  { name: "Gjakovë", zip_code: "50000" },
];

const labelClass = "block text-xs font-semibold text-gray-700 mb-1.5";
const inputClass =
  "w-full rounded-lg border bg-white px-3 h-[42px] text-sm shadow-sm transition placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)] disabled:bg-gray-50 disabled:text-gray-400";
const fieldClass = (hasError) =>
  `${inputClass} ${hasError ? "border-red-400" : "border-gray-300"}`;

const chevronBg = {
  backgroundImage:
    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%239CA3AF'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 011.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
  backgroundSize: "18px",
};

const selectFieldClass = (hasError) =>
  `${fieldClass(hasError)} appearance-none pr-10 cursor-pointer`;


const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
    ["link"],
  ],
};

export default function AddNotification() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchTextInput, setSearchTextInput] = useState("");
  const [searchList, setSearchList] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState([]);
  const [type, setType] = useState(null);
  const [notification, setNotification] = useState({ title: "", body: "", type: "" });
  const [htmlText, setHtmlText] = useState("");
  const [notifyEveryone, setNotifyEveryone] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});

  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  const { data: notificationTypes } = useGetNotificationTypesQuery();
  const { data: municipalities } = useGetMunicipalitiesQuery();
  const [searchUsers, { data: users }] = useLazySearchUsersQuery();
  const [sendNotification, { isLoading: isSending }] = useSendNotificationMutation();

  /* Image preview */
  useEffect(() => {
    if (image) setImagePreview(URL.createObjectURL(image));
  }, [image]);

  /* Sync HTML body into form state */
  useEffect(() => {
    setNotification((prev) => ({ ...prev, body: htmlText }));
  }, [htmlText]);

  /* Clear recipient error when any recipient is added */
  useEffect(() => {
    if (selectedUsers.length > 0 || selectedCities.length > 0 || selectedMunicipalities.length > 0) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.recipients;
        return next;
      });
    }
  }, [selectedUsers, selectedCities, selectedMunicipalities]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNotification((p) => ({ ...p, [name]: value }));
    if (value) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleQuillChange = (content) => {
    setHtmlText(content);
    if (content) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.body;
        return next;
      });
    }
  };

  const handleTypeChange = (value) => {
    setType(value);
    if (value) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.type;
        return next;
      });
    }
  };

  const handleRecipientsSearchChange = (e) => {
    const text = e.target.value;
    setSearchTextInput(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (text.length > 2) {
      debounceRef.current = setTimeout(async () => {
        const result = await searchUsers(text);
        if (result?.data?.data) setSearchList(result.data.data);
      }, 400);
    } else {
      setSearchList([]);
    }
  };

  const handleUserPick = (user) => {
    if (selectedUsers.some((u) => u.email === user.email)) {
      setSelectedUsers(selectedUsers.filter((u) => u.email !== user.email));
      return;
    }
    setSelectedUsers([...selectedUsers, user]);
    setSearchList([]);
    setSearchTextInput("");
  };

  const handleRegionChange = (value) => {
    if (value && value.length > 0) setSelectedUsers([]);
    setSelectedMunicipalities(value || []);
  };

  const removeUser = (email) =>
    setSelectedUsers(selectedUsers.filter((u) => u.email !== email));

  const removeCity = (zip_code) =>
    setSelectedCities(selectedCities.filter((c) => c.zip_code !== zip_code));

  const handleNotifyEveryoneChange = () => {
    setNotifyEveryone((v) => !v);
    setSelectedUsers([]);
    setSelectedCities([]);
    setSelectedMunicipalities([]);
    setSearchList([]);
    setSearchTextInput("");
    setErrors((prev) => {
      const next = { ...prev };
      delete next.recipients;
      return next;
    });
  };

  const handlePictureChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
    if (file) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.image;
        return next;
      });
    }
  };

  const handleClearImage = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    setImage(null);
    setImagePreview("");
  };

  const validate = () => {
    const e = {};
    if (!notification.title) e.title = "Titulli është i detyrueshëm";
    if (removeHtmlTags(notification.body || "").trim().length < 1)
      e.body = "Përmbajtja është e detyrueshme";
    if (!type) e.type = "Tipi është i detyrueshëm";
    if (
      selectedUsers.length < 1 &&
      selectedMunicipalities.length < 1 &&
      !notifyEveryone
    )
      e.recipients = "Zgjidh të paktën një marrës";

    setErrors(e);
    if (Object.keys(e).length > 0) {
      dispatch(openModalWithTimeout({ text: Object.values(e)[0], type: "error" }, 3000));
    }
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const notificationData = {
      title: notification.title,
      body: notification.body,
      data: {
        userIds: selectedUsers.map((u) => u.id),
        municipalities_no: selectedMunicipalities.map((m) => m.value),
        notifyEveryone,
      },
      type_id: type?.value,
    };

    try {
      const formData = new FormData();
      formData.append("notification", JSON.stringify(notificationData));
      if (image) formData.append("image", image);
      await sendNotification(formData).unwrap();
      dispatch(
        openModalWithTimeout({ text: "Njoftimi u dërgua me sukses!", type: "success" }, 3000)
      );
      navigate("/notifications");
    } catch (error) {
      const msg = error?.data?.message || "Dërgimi i njoftimit dështoi";
      dispatch(openModalWithTimeout({ text: msg, type: "error" }, 3000));
    }
  };

  const handleCancel = () => navigate("/notifications");

  return (
    <ListPageShell>
      <ListPageHeader
        eyebrow={{ icon: <LuBell size={18} />, label: "Njoftimet" }}
        title="Dërgo njoftim të ri"
        subtitle="Shkruaj njoftimin dhe zgjidh marrësit ose komunat e synuara."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition"
            >
              <FiX size={16} />
              Anulo
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSending}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <ClipLoader color="white" size={14} />
                  Po dërgohet...
                </>
              ) : (
                <>
                  <LuSend size={16} />
                  Dërgo
                </>
              )}
            </button>
          </div>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] p-5 sm:p-6 flex flex-col gap-5">
          {/* Title + Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="title" className={labelClass}>Titulli</label>
              <input
                type="text"
                id="title"
                name="title"
                value={notification.title}
                onChange={handleChange}
                className={fieldClass(errors.title)}
                placeholder="Shkruaj titullin e njoftimit"
              />
              {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
            </div>
            <div>
              <label htmlFor="type" className={labelClass}>Tipi</label>
              <select
                id="type"
                name="type"
                value={type?.value || ""}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return handleTypeChange(null);
                  const opt = notificationTypes?.data?.find(
                    (v) => String(v.id) === id
                  );
                  handleTypeChange(opt ? { value: opt.id, label: opt.name } : null);
                }}
                className={selectFieldClass(errors.type)}
                style={chevronBg}
              >
                <option value="">Zgjidh tipin</option>
                {notificationTypes?.data?.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}
                  </option>
                ))}
              </select>
              {errors.type && <p className="mt-1 text-xs text-red-600">{errors.type}</p>}
            </div>
          </div>

          {/* Recipients search + Municipalities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label htmlFor="recipients" className={labelClass}>Marrësit</label>
              <input
                type="text"
                id="recipients"
                name="recipients"
                value={searchTextInput}
                onChange={handleRecipientsSearchChange}
                disabled={notifyEveryone || selectedMunicipalities.length > 0}
                autoComplete="off"
                className={fieldClass(errors.recipients)}
                placeholder="Kërko sipas email-it..."
              />
              {searchList.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto z-40 rounded-lg border border-gray-200 bg-white shadow-lg">
                  {searchList.map((u, i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => handleUserPick(u)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition border-b border-gray-100 last:border-b-0"
                    >
                      {u.email}
                    </button>
                  ))}
                </div>
              )}
              {errors.recipients && (
                <p className="mt-1 text-xs text-red-600">{errors.recipients}</p>
              )}
            </div>
            <div>
              <label htmlFor="municipality_no" className={labelClass}>
                Marrësit sipas komunës
              </label>
              <select
                id="municipality_no"
                name="municipality_no"
                value=""
                disabled={notifyEveryone}
                onChange={(e) => {
                  const num = e.target.value;
                  if (!num) return;
                  const m = municipalities?.data?.find(
                    (x) => String(x.municipality_no) === num
                  );
                  if (
                    m &&
                    !selectedMunicipalities.some((x) => x.value === m.municipality_no)
                  ) {
                    handleRegionChange([
                      ...selectedMunicipalities,
                      { value: m.municipality_no, label: m.municipality_name },
                    ]);
                  }
                }}
                className={selectFieldClass(false)}
                style={chevronBg}
              >
                <option value="">Të gjitha komunat</option>
                {municipalities?.data?.map((m) => (
                  <option
                    key={m.municipality_no}
                    value={m.municipality_no}
                    disabled={selectedMunicipalities.some(
                      (x) => x.value === m.municipality_no
                    )}
                  >
                    {m.municipality_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notify everyone toggle */}
          <label className="inline-flex items-center gap-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              checked={notifyEveryone}
              onChange={handleNotifyEveryoneChange}
              className="w-4 h-4 accent-[var(--secondary-color)]"
            />
            <span className="text-xs font-semibold text-gray-700">
              Njofto të gjithë përdoruesit
            </span>
          </label>

          {/* Selected recipients chips */}
          {(selectedUsers.length > 0 ||
            selectedCities.length > 0 ||
            selectedMunicipalities.length > 0) && (
            <div className="flex items-center gap-2 flex-wrap">
              {selectedUsers.map((u, i) => (
                <span
                  key={`u-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200"
                >
                  {u.email}
                  <LuX
                    size={14}
                    className="text-gray-400 hover:text-red-600 cursor-pointer"
                    onClick={() => removeUser(u.email)}
                  />
                </span>
              ))}
              {selectedMunicipalities.map((m, i) => (
                <span
                  key={`m-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200"
                >
                  {m.label}
                  <LuX
                    size={14}
                    className="text-gray-400 hover:text-red-600 cursor-pointer"
                    onClick={() =>
                      handleRegionChange(
                        selectedMunicipalities.filter((x) => x.value !== m.value)
                      )
                    }
                  />
                </span>
              ))}
              {selectedCities.map((c, i) => (
                <span
                  key={`c-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200"
                >
                  Të gjithë nga {c.name}
                  <LuX
                    size={14}
                    className="text-gray-400 hover:text-red-600 cursor-pointer"
                    onClick={() => removeCity(c.zip_code)}
                  />
                </span>
              ))}
            </div>
          )}

          {/* Body editor */}
          <div>
            <label className={labelClass}>Përmbajtja</label>
            <div
              className={`rounded-lg border bg-white overflow-hidden ${
                errors.body ? "border-red-400" : "border-gray-300"
              }`}
            >
              <ReactQuill
                theme="snow"
                modules={quillModules}
                value={htmlText}
                onChange={handleQuillChange}
                style={{ height: 360 }}
                className="modern-quill"
              />
            </div>
            {errors.body && <p className="mt-1 text-xs text-red-600">{errors.body}</p>}
          </div>

          {/* Image upload */}
          <div>
            <label className={labelClass}>Foto (opsionale)</label>
            <input
              id="files"
              name="files"
              type="file"
              ref={fileInputRef}
              accept="image/png, image/jpg, image/jpeg, image/svg"
              onChange={handlePictureChange}
              className="hidden"
            />
            {image ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-2 pr-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img
                    src={imagePreview}
                    alt=""
                    className="w-14 h-14 object-cover rounded-md ring-1 ring-gray-200"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {image.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(image.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearImage}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-red-50 hover:text-red-600 hover:border-red-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition"
                >
                  <FiTrash2 size={14} />
                  Hiq
                </button>
              </div>
            ) : (
              <label
                htmlFor="files"
                className="block cursor-pointer rounded-lg border-2 border-dashed border-gray-300 bg-gray-50/60 hover:bg-gray-50 hover:border-[var(--secondary-color)] transition py-8 px-4"
              >
                <div className="flex flex-col items-center text-center gap-2">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-500">
                    <LuUploadCloud size={22} />
                  </span>
                  <div className="text-sm">
                    <span className="font-semibold text-[var(--secondary-color)]">
                      Kliko për të ngarkuar
                    </span>{" "}
                    <span className="text-gray-500">ose tërhiq dhe lësho</span>
                  </div>
                  <span className="text-xs text-gray-400">PNG, JPG, JPEG, SVG</span>
                </div>
              </label>
            )}
            {errors.image && <p className="mt-1 text-xs text-red-600">{errors.image}</p>}
          </div>
        </div>

        {/* Hidden submit so Enter triggers send */}
        <button type="submit" className="hidden" />
      </form>
    </ListPageShell>
  );
}
